import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  gradient,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { cleanupText } from '../../api/iq-client';
import { FREE_TEXT_MAX } from '../../constants/text-limits';

// WP-10. The Lobbero IQ tidy-up hint for one free-text log field.
//
// AURORA, because this is model output and the aurora gradient is reserved for
// exactly that (design_system §2.1 "AI accent — Lobbero IQ only").
//
// THREE RULES THIS COMPONENT EXISTS TO KEEP:
//   1. It NEVER writes. Apply calls back into the editor; the concierge presses
//      it or ignores it, and nothing reaches the log either way until they save.
//   2. It shows the ORIGINAL next to the SUGGESTION. Accepting a rewrite you
//      cannot see against what you wrote is not review, it is a coin toss.
//   3. It renders NOTHING at all when there is no suggestion — including when
//      the feature is off, the budget is gone, or the model is unavailable.
//      A hint that says "no hint available" is worse than silence at a desk.

const Panel = styled.section`
  width: 70%;
  max-width: 600px;
  background: ${gradient.auroraSoft}, ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.sm};
  margin-bottom: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  ${down('sm')} {
    width: 100%;
  }
`;

const Eyebrow = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  background: ${gradient.aurora};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.xs};

  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

const ColumnLabel = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

const Body = styled.p<{ muted?: boolean }>`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${({ muted }) => (muted ? palette.slate : palette.ink)};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.sm};
  padding: ${spacing.xs};
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.xs};
`;

const Ghost = styled.button`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  background: transparent;
  border: none;
  border-radius: ${radius.sm};
  padding: ${spacing.half} ${spacing.xs};
  cursor: pointer;
  transition: ${transition.base};

  &:hover {
    color: ${palette.ink};
  }

  /* KEYBOARD GETS WHAT THE POINTER GETS — this styled :hover and nothing for
     focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

const Primary = styled.button`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.white};
  background: ${gradient.aurora};
  border: none;
  border-radius: ${radius.sm};
  padding: ${spacing.half} ${spacing.sm};
  cursor: pointer;
  transition: ${transition.base};

  &:hover {
    filter: brightness(1.05);
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

// Quill hands us HTML. The panel compares what a person actually reads.
const asPlainText = (html: string): string => {
  const el = document.createElement('div');
  el.innerHTML = html ?? '';

  return (el.textContent || '').replace(/\s+/g, ' ').trim();
};

interface IqCleanupHintProps {
  // The current editor contents, as Quill's HTML.
  value: string;
  // Which log column this is, for the ledger and the prompt.
  field: string;
  buildingId?: number;
  // Called with the plain text the concierge accepted.
  onApply: (text: string) => void;
  // The caller decides whether IQ is available at all; when false this renders
  // nothing and issues no request.
  enabled?: boolean;
}

// Long enough to be worth tidying. Below this the model has nothing to work with
// and the request is pure cost.
const MIN_CHARS = 24;
const DEBOUNCE_MS = 1200;

export const IqCleanupHint: React.FC<IqCleanupHintProps> = ({
  value,
  field,
  buildingId,
  onApply,
  enabled = true,
}) => {
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [original, setOriginal] = useState<string>('');
  const [dismissed, setDismissed] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);

  const plain = asPlainText(value);

  const run = useCallback(
    async (text: string) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const r = await cleanupText(
        { building_id: buildingId, text, field },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;
      // suggestion === null covers every refusal the server can make: feature
      // off, budget gone, model unavailable, or a suggestion the post-validation
      // guard discarded. All of them render as nothing.
      setSuggestion(r.suggestion);
      setOriginal(r.original);
    },
    [buildingId, field]
  );

  useEffect(() => {
    if (!enabled || plain.length < MIN_CHARS || plain.length > FREE_TEXT_MAX) {
      setSuggestion(null);

      return;
    }
    if (plain === dismissed) return;
    // DEBOUNCED, and the server's per-org burst cap is the real backstop behind
    // it (WP-9): this is a per-keystroke surface and must not be able to spend
    // the org's summary budget one character at a time.
    const t = setTimeout(() => {
      void run(plain);
    }, DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [plain, enabled, dismissed, run]);

  useEffect(() => () => abortRef.current?.abort(), []);

  if (!suggestion) return null;

  return (
    <Panel>
      <Eyebrow>Lobbero IQ suggestion</Eyebrow>
      <Columns>
        <Column>
          <ColumnLabel>What you wrote</ColumnLabel>
          <Body muted>{original}</Body>
        </Column>
        <Column>
          <ColumnLabel>Suggested</ColumnLabel>
          <Body>{suggestion}</Body>
        </Column>
      </Columns>
      <Actions>
        <Ghost
          type="button"
          onClick={() => {
            setDismissed(plain);
            setSuggestion(null);
          }}
        >
          Keep mine
        </Ghost>
        <Primary
          type="button"
          onClick={() => {
            onApply(suggestion);
            setDismissed(asPlainText(suggestion));
            setSuggestion(null);
          }}
        >
          Use this
        </Primary>
      </Actions>
    </Panel>
  );
};
